import React, { useEffect, useState } from "react";
import { LeftOutlined, InfoCircleOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Form,
  Input,
  Switch,
  Row,
  Col,
  notification,
  Tag,
  Modal,
  Tooltip,
} from "antd";
import { useForm } from "antd/lib/form/Form";
import { useTranslation } from "react-i18next";
import ThirdPartyIntegration from "services/thirdPartyIntegrationService";
import PaymentMethodService from "services/paymentMethodService";
import { THIRDPARTY_CODE } from "constants/app";
import { getThirdPartyIdFromStationIntegrationList } from "./helpers/thirdPartyIntegrationHelper";
import "./TelegramThirdParty.scss";
import "./TingeeThirdParty.scss";

const VietQr = ({ id, setOpenContent, setId }) => {
  const { t: translation } = useTranslation();
  const [form] = useForm();
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [isTested, setIsTested] = useState(true);

  useEffect(() => {
    id && handleGetData(id);
  }, [id]);

  const handleGetData = async (targetId = id) => {
    if (!targetId) return;
    try {
      setLoading(true);
      const res = await ThirdPartyIntegration.getThirdPartyById({ id: targetId });
      const partyRequiredData = JSON.parse(res?.partyRequiredData || "{}");
      const integrationMetadata = JSON.parse(res?.integrationMetadata || "{}");
      form.setFieldsValue({
        username: partyRequiredData?.username,
        password: partyRequiredData?.password,
        bankAccount: integrationMetadata?.bankAccount,
        bankCode: integrationMetadata?.bankCode,
        userBankName: integrationMetadata?.userBankName,
        enablePaymentCollection: integrationMetadata?.enablePaymentCollection,
        terminalCode: integrationMetadata?.terminalCode,
        serialNumber: integrationMetadata?.serialNumber,
        devicePassword: integrationMetadata?.devicePassword,
        enableQrDisplay: integrationMetadata?.enableQrDisplay === 1,
        isConnected: res.partyActiveStatus ? 1 : 0,
        isEnable: res?.partyEnableStatus,
      });
      setIsActive(res.partyActiveStatus);
      setIsTested(true);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      await form.validateFields();
      if (!form.getFieldValue("isConnected")) {
        setIsOpen(true);
        return;
      }
      handleUpdateConfig();
    } catch (error) {}
  };

  const handleUpdateConfig = async (forceConnected = false) => {
    try {
      const values = await form.getFieldsValue();
      const isConnectedFieldValue = form.getFieldValue("isConnected");
      const isConnected = forceConnected
        ? 1
        : (isConnectedFieldValue ? 1 : 0);
      const isEnable = values.isEnable ? 1 : 0;
      const paymentMethodEnable = isConnected && isEnable ? 1 : 0;
      const payload = {
        data: {
          partyRequiredData: {
            username: values.username,
            password: values.password,
          },
          partyActiveStatus: isConnected ? 1 : 0,
          partyEnableStatus: isEnable,
          integrationMetadata: {
            // enablePaymentCollection: 0, // Bật chế độ thu hộ
            bankAccount: values.bankAccount,
            userBankName: values.userBankName,
            bankCode: values.bankCode,
            terminalCode: values.terminalCode,
            serialNumber: values.serialNumber,
            devicePassword: values.devicePassword,
            enableQrDisplay: values.enableQrDisplay ? 1 : 0,
          },
        },
      };
      await ThirdPartyIntegration.updateConfigsVietQR(payload);
      setIsActive(isConnected);
      const updatedThirdPartyId = await getThirdPartyIdFromStationIntegrationList(THIRDPARTY_CODE.BANKING_VIETQR);
      if (!updatedThirdPartyId) {
        throw new Error('VIETQR_ID_NOT_FOUND_FROM_LIST');
      }

      const paymentMethodListResp = await PaymentMethodService.getAdvanceUserList({
        filter: {
          paymentMethodCode: THIRDPARTY_CODE.BANKING_VIETQR,
        },
        skip: 0,
        limit: 20,
      });
      const paymentMethodList = Array.isArray(paymentMethodListResp?.data) ? paymentMethodListResp.data : [];
      const targetPaymentMethod =
        paymentMethodList.find((item) => Number(item?.thirdPartyId) === Number(updatedThirdPartyId))
        || paymentMethodList[0];
      if (targetPaymentMethod?.paymentMethodId) {
        await PaymentMethodService.advanceUserUpdateById({
          id: targetPaymentMethod.paymentMethodId,
          data: {
            paymentMethodIdentityNumber: values.bankAccount,
            paymentMethodReceiverName: values.userBankName,
            paymentMethodEnable,
          },
        });
      }
      if (Number(updatedThirdPartyId) !== Number(id)) {
        setId(updatedThirdPartyId);
      } else {
        await handleGetData(updatedThirdPartyId);
      }
      notification["success"]({
        message: "",
        description: translation("vietqr.updateSuccess"),
      });
    } catch (error) {
      notification["error"]({
        message: "",
        description: translation("landing.error"),
      });
    }
  };

  const handleTestTransaction = async () => {
    try {
      const values = await form.getFieldsValue();
      const payload = {
        username: values.username,
        password: values.password,
        bankAccount: values.bankAccount,
        bankCode: values.bankCode,
        userBankName: values.userBankName,
      };
      await ThirdPartyIntegration.testConfigsVietQR(payload);
      notification["success"]({
        message: "",
        description: translation("vietqr.successMessage"),
      });
      form.setFieldsValue({ isConnected: 1 });
      setIsActive(1);
      setIsTested(false);
      setIsOpen(false);
      await handleUpdateConfig(true);
    } catch (error) {
      notification["error"]({
        message: "",
        description: translation("vietqr.errorMessage"),
      });
    }
  };

  return (
    <>
      <div className="tingee-noti">
        <Button
          type="default"
          icon={<LeftOutlined />}
          onClick={() => setOpenContent("")}
        />
        <Card loading={loading} className="telegram-noti_content">
          <div className="section-title mb-2">
            {translation("vietqr.title")}
          </div>
          <Form form={form} layout="vertical">
            <Row gutter={[48, 16]}>
              <Col
                xs={{ order: 2, span: 24 }}
                md={{ order: 1, span: 10 }}
                span={10}
              >
                <div className="section-title mb-2 mt-3">{translation("vietqr.accountSetup")}</div>
                <Form.Item
                  required
                  label={translation("vietqr.username")}
                  name="username"
                  rules={[{ required: true, message: translation("isReq") }]}
                >
                  <Input type="text"/>
                </Form.Item>
                <Form.Item
                  required
                  label={translation("vietqr.password")}
                  name="password"
                  rules={[{ required: true, message: translation("isReq") }]}
                >
                  <Input type="text"/>
                </Form.Item>
                <Form.Item
                  required
                  label={translation("vietqr.bankAccount")}
                  name="bankAccount"
                  rules={[{ required: true, message: translation("isReq") }]}
                >
                  <Input type="text"/>
                </Form.Item>
                <Form.Item
                  required
                  label={translation("vietqr.bankCode")}
                  name="bankCode"
                  rules={[{ required: true, message: translation("isReq") }]}
                >
                  <Input type="text"/>
                </Form.Item>
                <Form.Item
                  required
                  label={translation("vietqr.userBankName")}
                  name="userBankName"
                  rules={[{ required: true, message: translation("isReq") }]}
                >
                  <Input type="text"/>
                </Form.Item>
                <Form.Item
                  label={translation("vietqr.customerPay")}
                  name="customerPay"
                  valuePropName="checked"
                  initialValue={true}
                >
                  <Switch defaultChecked={isActive}/>
                </Form.Item>
                <Form.Item
                  label={translation("vietqr.collectionTTDK")}
                  name="enablePaymentCollection"
                  valuePropName="checked"
                  // initialValue={false}
                >
                  <Switch defaultChecked={isActive}/>
                </Form.Item>
                <Form.Item
                  label={translation("telegram.active")}
                  name="isEnable"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
                <Form.Item label={translation("vietqr.connectionStatus")}>
                  <Tag color={form.getFieldValue("isConnected") ? "green" : ""}>
                    {form.getFieldValue("isConnected")
                      ? translation("vietqr.connected")
                      : translation("vietqr.notConnected")}
                  </Tag>
                </Form.Item>
              </Col>
              <Col
                xs={{ order: 2, span: 24 }}
                md={{ order: 1, span: 10 }}
                span={10}
              >
                <div className="section-title mb-2 mt-3 device-setup-title">
                  {translation("vietqr.deviceSetup")}
                  <Tooltip title={translation("vietqr.deviceSetupHint")}>
                    <InfoCircleOutlined className="device-setup-icon" />
                  </Tooltip>
                </div>
                <Form.Item
                  label={translation("vietqr.terminalCode")}
                  name="terminalCode"
                >
                  <Input 
                    type="text" 
                    disabled={!form.getFieldValue("isConnected")}
                    onChange={(e) => {
                      const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                      form.setFieldsValue({ terminalCode: value });
                    }}
                  />
                </Form.Item>
                <Form.Item
                  label={translation("vietqr.serialNumber")}
                  name="serialNumber"
                >
                  <Input 
                    type="text" 
                    disabled={!form.getFieldValue("isConnected")}
                    onChange={(e) => {
                      const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                      form.setFieldsValue({ serialNumber: value });
                    }}
                  />
                </Form.Item>
                <Form.Item
                  label={translation("vietqr.devicePassword")}
                  name="devicePassword"
                  dependencies={['terminalCode', 'serialNumber']}
                  rules={[
                    { validator: async (_, value) => {
                        if (value) {
                          if (!form.getFieldValue('terminalCode') || !form.getFieldValue('serialNumber')) 
                            throw new Error(translation('vietqr.devicePasswordValidation'));
                        }
                      }
                    }
                  ]}
                >
                  <Input type="text" disabled={!form.getFieldValue("isConnected")} />
                </Form.Item>
                <Form.Item
                  label={translation("vietqr.enableQrDisplay")}
                  name="enableQrDisplay"
                  valuePropName="checked"
                >
                  <Switch disabled={!form.getFieldValue("isConnected")} />
                </Form.Item>
              </Col>
            </Row>
            <div className="text-left mt-3">
              <Button type="primary" onClick={handleSubmit}>
                {translation("vietqr.save")}
              </Button>
            </div>
          </Form>
        </Card>
      </div>

      <Modal
        visible={isOpen}
        title={translation("vietqr.sendVD")}
        onCancel={() => setIsOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setIsOpen(false)}>
            {translation("vietqr.skip")}
          </Button>,
          <Button key="test" type="primary" onClick={handleTestTransaction}>
            {translation("vietqr.sendVD")}
          </Button>,
        ]}
      >
        <p>{translation("vietqr.testTransactionMessage")}</p>
      </Modal>
    </>
  );
};

export default VietQr;
